import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';
import { DatePicker, TextField } from 'office-ui-fabric-react';
import { NotificationHandler } from '@app/Components/ToastNotification';

import { securityService } from '@app/Services/SecurityService';
import ApiService from '@app/Services/ApiService';
import DateTimeService from '@app/Services/DateTimeService';
import { ApiUrls, Permissions } from '@app/AppConstants';
import { PlanningLicenseInactivationCreateModel } from '@app/Models';
import { DateTimePickerService } from '@app/Services/DateTimePickerService';
import { ModalWindow, ModalDialogOptions, ModalButtonType, IModalDialogContent } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';

type LicenseInactivationProps = {
    licenseDateFrom?: Date | undefined;
    licenseDateTo?: Date | undefined;
    licenseId: string;
};

@observer
export class NewLicenseInactivationDialog extends React.Component<LicenseInactivationProps, {}> implements IModalDialogContent<void> {
    _store: NewLicenseInactivationStore;

    constructor(props: LicenseInactivationProps) {
        super(props);
        this._store = new NewLicenseInactivationStore(props);
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        const { hasPermission } = securityService;
        return {
            title: 'New License Inactivation',
            width: '300px',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }, {
                type: ModalButtonType.Save,
                isDisabled: !this._store.isValidForm || !hasPermission(Permissions.EditObjectPersonLicenseInactivation, !AzureAuth.isReadOnly),
                onClick: async () => {
                    if (await this._store.onSave()){
                        window.close(ModalButtonType.Save);
                    }
                }
            }],
        };
    }

    render() {
        return (
            <>
                <DatePicker
                    label="Date From"
                    className={this.dateFromClassName}
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this._store.onSelectFromDate}
                    isRequired={true}
                    value={this._store.dateFrom || undefined}
                    maxDate={this._store.licenseDateTo}
                    minDate={this._store.licenseDateFrom} />
                {!this._store.isDateFromInRange && <span className="ms-TextField-errorMessage errorMessage">Invalid date range</span>}
                <DatePicker
                    label="Date To"
                    className={this.dateToClassName}
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this._store.onSelectToDate}
                    value={this._store.dateTo || undefined}
                    maxDate={this._store.licenseDateTo}
                    minDate={this._store.licenseDateFrom} />
                {!this._store.isDateToInRange && <span className="ms-TextField-errorMessage errorMessage">Invalid date range</span>}
                <TextField
                    label="Comment"
                    onChange={this._store.onCommentChange}
                />
                {this._store.validationMessage && <p className="ms-TextField-errorMessage errorMessage">{this._store.validationMessage}</p>}
            </>
        );
    }

    @computed
    get dateFromClassName() {
        return 'date-field' + (!this._store.isDateFromInRange ? ' invalid' : '');
    }

    @computed
    get dateToClassName() {
        return 'date-field' + (!this._store.isDateToInRange ? ' invalid' : '');
    }
}

class NewLicenseInactivationStore {
    @observable dateFrom: Date | null;
    @observable dateTo: Date | null;

    @observable isDateFromInRange: boolean = true;
    @observable isDateToInRange: boolean = true;

    @observable comment: string = '';
    @observable validationMessage: string | null = '';

    licenseId: string;
    licenseDateFrom: Date | undefined;
    licenseDateTo: Date | undefined;

    constructor(props: LicenseInactivationProps) {
        this.licenseId = props.licenseId;
        this.licenseDateFrom = props.licenseDateFrom;
        this.licenseDateTo = props.licenseDateTo;
    }

    @action
    onSelectFromDate = (date: Date | null | undefined): void => {
        this.dateFrom = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
        this.isDateFromInRange = this.dateFrom ? this.validateDate(this.dateFrom) : true;
        this.isDateToInRange = this.dateTo ? this.validateDate(this.dateTo) : true;
    };

    @action
    onSelectToDate = (date: Date | null | undefined): void => {
        this.dateTo = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
        this.isDateToInRange = this.dateTo ? this.validateDate(this.dateTo) : true;
        this.isDateFromInRange = this.dateFrom ? this.validateDate(this.dateFrom) : true;
    };

    @action
    onCommentChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this.comment = newValue ? newValue : '';
    };

    @action
    validateDate = (date: Date): boolean => {
        if (this.licenseDateFrom) {
            const isBeforeOrSameDate = DateTimeService.isBeforeDate(this.licenseDateFrom, date) || DateTimeService.isSameDate(this.licenseDateFrom, date);
            if (!isBeforeOrSameDate) return false;
        }

        if (this.licenseDateTo) {
            const isBeforeOrSamedate = DateTimeService.isBeforeDate(date, this.licenseDateTo) || DateTimeService.isSameDate(date, this.licenseDateTo);
            if (!isBeforeOrSamedate) return false;
        }

        if (this.dateTo && this.dateFrom) return (DateTimeService.isBeforeDate(this.dateFrom, this.dateTo) || DateTimeService.isSameDate(this.dateFrom, this.dateTo));

        return true;
    };

    @computed
    get isValidForm() {
        return (!this.dateFrom || !this.isDateFromInRange || !this.isDateToInRange) ? false : true;
    }

    @action.bound
    async onSave() {
        const params: PlanningLicenseInactivationCreateModel = {
            personLicenseId: this.licenseId,
            dateFrom: this.dateFrom as Date,
            dateTo: this.dateTo as Date,
            comment: this.comment
        };
        const { data } = await ApiService.postData(ApiUrls.PersonLicenseInactivationUrl, params);
        if (!data.hasErrors) {
            this.validationMessage = null;
            NotificationHandler.showSuccess('New License Inactivation was added', 'Success message');
            return true;
        } else {
            this.validationMessage = data.errors.join(' ');
        }
        return false;
    }
}